import React, { useRef, useState, useEffect } from "react";

/**
 * DatePartsInput
 * --------------
 * Day / Month / Year text boxes (DD MM YYYY).
 * • Auto-advances focus when max length is reached.
 * • Calls `onChange(fullIsoDate)` whenever *any* part changes
 *   – returns an ISO-compatible string **yyyy-mm-dd** or `""` if incomplete.
 *
 * Props
 * -----
 * • value      – current combined value (“yyyy-mm-dd” or “”) from parent form
 * • onChange   – (e) => void  ← identical API to normal <input/> handler
 * • id         – field id (used for name/ids of the three inputs)
 * • required   – mark all three boxes as required
 * • className  – extra Tailwind / CSS classes for the wrapper
 */
export default function DatePartsInput({
  value = "",
  onChange,
  id,
  required,
  className = "",
}) {
  const [parts, setParts] = useState({ dd: "", mm: "", yyyy: "" });

  const dRef = useRef(null);
  const mRef = useRef(null);
  const yRef = useRef(null);

  /* keep internal state in sync when parent value changes */
  useEffect(() => {
    if (!value) return;
    const [yyyy, mm, dd] = value.split("-");
    setParts({ dd: dd ?? "", mm: mm ?? "", yyyy: yyyy ?? "" });
  }, [value]);

  const handlePartChange = (key, maxLen, nextRef) => (e) => {
    const raw = e.target.value.replace(/\D/g, ""); // numbers only
    const trimmed = raw.slice(0, maxLen);

    setParts((p) => ({ ...p, [key]: trimmed }));

    /* auto-advance focus */
    if (trimmed.length === maxLen && nextRef?.current) {
      nextRef.current.focus();
    }

    /* assemble ISO string & fire synthetic event */
    const { dd, mm, yyyy } = { ...parts, [key]: trimmed };
    const full =
      dd.length === 2 && mm.length === 2 && yyyy.length === 4
        ? `${yyyy}-${mm}-${dd}`
        : "";

    /* mimic native <input/> event shape so parent handler stays the same */
    onChange({
      target: { name: id, value: full },
    });
  };

  return (
    <div className={`flex gap-2 ${className}`}>
      <input
        ref={dRef}
        type="text"
        inputMode="numeric"
        pattern="\d*"
        placeholder="DD"
        value={parts.dd}
        onChange={handlePartChange("dd", 2, mRef)}
        maxLength={2}
        required={required}
        className="custom-input w-16 text-center"
      />
      <input
        ref={mRef}
        type="text"
        inputMode="numeric"
        pattern="\d*"
        placeholder="MM"
        value={parts.mm}
        onChange={handlePartChange("mm", 2, yRef)}
        maxLength={2}
        required={required}
        className="custom-input w-16 text-center"
      />
      <input
        ref={yRef}
        type="text"
        inputMode="numeric"
        pattern="\d*"
        placeholder="YYYY"
        value={parts.yyyy}
        onChange={handlePartChange("yyyy", 4)}
        maxLength={4}
        required={required}
        className="custom-input w-24 text-center"
      />
    </div>
  );
}
